import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { endpoints } from "../lib/api";
import { Overline, Section, PrimaryButton } from "../components/UI";
import { SEO } from "../components/SEO";
import { toast, Toaster } from "sonner";
import { ShieldCheck, Phone, MessageCircle } from "lucide-react";
import { CONTACT } from "../data/contactDetails";

const TYPES = {
  appointment: { title: "Book a Consultation", intro: "Appointment requests are reviewed by our team. We will contact you regarding availability and the next steps." },
  referral: { title: "Refer a Patient", intro: "Structured referral pathway for practising physicians." },
  second_opinion: { title: "Request a Second Opinion", intro: "A considered clinical review, with your records." },
  contact: { title: "Get in Touch", intro: "Non-clinical enquiries, media requests, and general questions." },
};

export function Contact() {
  const [params] = useSearchParams();
  const type = params.get("type") || "appointment";
  const cfg = TYPES[type] || TYPES.contact;

  const [form, setForm] = useState({
    type, name: "", phone: "", email: "", city: "", consultation_type: "",
    preferred_date: "", preferred_time_slot: "", reason_short: "",
    is_patient_or_doctor: type === "referral" ? "doctor" : "patient",
    consent_to_contact: false, referring_doctor_name: "", referring_doctor_registration: "",
    source_page: type,
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent_to_contact) return toast.error("Please provide consent to be contacted.");
    if (!form.name || !form.phone) return toast.error("Please fill in your name and phone.");
    if (type === "appointment" && !form.consultation_type) return toast.error("Please choose a consultation type.");
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return toast.error("Please enter a valid email address, or leave the field empty.");
    setSubmitting(true);
    try {
      await endpoints.createEnquiry({ ...form, type });
      // Google Ads conversion event — fires only after a genuinely successful
      // consultation-enquiry submission confirmed by the backend. No patient
      // data is sent to Google; only the conversion destination ID is passed.
      if (type === "appointment" && typeof window.gtag === "function") {
        window.gtag('event', 'conversion', {
          send_to: 'AW-18482080206/yo98CNSTzoodEM7T-OxE'
        });
      }
      setSubmitted(true);
      toast.success("Received. Our team will contact you regarding availability and the next steps.");
    } catch (err) {
      toast.error("Something went wrong. Please try again or use WhatsApp.");
    } finally { setSubmitting(false); }
  };

  return (
    <Section>
      <SEO/>
      <Toaster richColors position="top-right"/>
      <div className="grid md:grid-cols-12 gap-12">
        <div className="md:col-span-7">
          <Overline>{type.replace("_", " ")}</Overline>
          <h1 className="font-serif text-4xl md:text-5xl text-navy mt-3 tracking-tight leading-[1.1]">{cfg.title}</h1>
          <p className="text-lg text-stone2 mt-4 max-w-xl">{cfg.intro}</p>

          {submitted ? (
            <div className="mt-10 bg-white border border-prestige/40 p-8" data-testid="enquiry-success">
              <div className="flex items-center gap-2 mono text-xs uppercase tracking-widest text-prestige"><ShieldCheck size={14} strokeWidth={1.5}/> Received</div>
              <div className="font-serif text-3xl text-navy mt-3">Thank you.</div>
              <p className="text-stone2 mt-3">Received. Our team will contact you regarding availability and the next steps.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-10 space-y-5" data-testid="enquiry-form">
              <Row>
                <Field label="Full Name *" testid="ef-name">
                  <input value={form.name} onChange={e => set("name", e.target.value)} className={inputCls} required data-testid="input-name"/>
                </Field>
                <Field label="Phone *" testid="ef-phone">
                  <input type="tel" value={form.phone} onChange={e => set("phone", e.target.value)} className={inputCls} required data-testid="input-phone"/>
                </Field>
              </Row>
              <Row>
                <Field label="Email" testid="ef-email">
                  <input type="email" value={form.email} onChange={e => set("email", e.target.value)} className={inputCls} data-testid="input-email"/>
                </Field>
                <Field label="City" testid="ef-city">
                  <input value={form.city} onChange={e => set("city", e.target.value)} className={inputCls} data-testid="input-city"/>
                </Field>
              </Row>
              {type === "appointment" && (
                <Row>
                  <Field label="Consultation Type">
                    <select value={form.consultation_type} onChange={e => set("consultation_type", e.target.value)} className={inputCls} data-testid="input-consultation-type">
                      <option value="">Select consultation type</option>
                      <option>In-person consultation — New Delhi</option>
                      <option>In-person consultation — Ambala</option>
                      <option>Teleconsultation</option>
                      <option>Not sure — please guide me</option>
                    </select>
                  </Field>
                  <Field label="Preferred Date">
                    <input type="date" value={form.preferred_date} onChange={e => set("preferred_date", e.target.value)} className={inputCls} data-testid="input-date"/>
                  </Field>
                </Row>
              )}
              {(type === "referral" || type === "second_opinion") && (
                <Row>
                  <Field label="Referring Doctor Name">
                    <input value={form.referring_doctor_name} onChange={e => set("referring_doctor_name", e.target.value)} className={inputCls} data-testid="input-ref-name"/>
                  </Field>
                  <Field label="Medical Registration No.">
                    <input value={form.referring_doctor_registration} onChange={e => set("referring_doctor_registration", e.target.value)} className={inputCls} data-testid="input-ref-reg"/>
                  </Field>
                </Row>
              )}
              <Field label="Broad reason for consultation">
                <textarea value={form.reason_short} onChange={e => set("reason_short", e.target.value)} className={`${inputCls} min-h-[110px]`} placeholder="Please do not share detailed medical history here — this website is not a secure clinical channel." data-testid="input-reason"/>
              </Field>
              <div
                data-testid="emergency-warning"
                role="note"
                className="border-l-2 border-terracotta bg-terracotta/5 px-4 py-3 text-[13.5px] text-navy leading-relaxed"
              >
                <strong className="font-medium">This form is not monitored for emergencies.</strong>{" "}
                If you have urgent symptoms, please seek immediate medical care.
              </div>
              <label className="flex items-start gap-3 text-sm text-stone2 cursor-pointer" data-testid="consent-label">
                <input type="checkbox" checked={form.consent_to_contact} onChange={e => set("consent_to_contact", e.target.checked)} className="mt-1" data-testid="input-consent"/>
                <span>I consent to be contacted by the clinic team using the details above regarding my enquiry. I understand this website is for scheduling only and not a secure clinical channel.</span>
              </label>
              <PrimaryButton onClick={()=>{}} testid="submit-enquiry" type="submit" className={submitting ? "opacity-70 pointer-events-none" : ""}>
                {submitting ? "Submitting…" : "Submit Enquiry"}
              </PrimaryButton>
            </form>
          )}
        </div>

        <aside className="md:col-span-5 space-y-6">
          <div className="bg-navy text-white p-8">
            <div className="mono text-[10px] uppercase tracking-widest text-prestige-soft">What happens next</div>
            <ol className="mt-4 space-y-4 text-white/85">
              <Step n="1" title="Enquiry received" text="We acknowledge every submission personally — no bots."/>
              <Step n="2" title="Team review" text="Our clinical coordinator reviews the enquiry and gets back regarding availability."/>
              <Step n="3" title="Next steps shared" text="Availability and the appropriate way to proceed are shared by the team."/>
            </ol>
          </div>
          <div className="bg-white border border-line p-6 space-y-6" data-testid="contact-side-numbers">
            <div>
              <div className="mono text-[10px] uppercase tracking-widest text-prestige" data-testid="side-primary-label">
                {CONTACT.primary.label}
              </div>
              <div className="mt-3 space-y-2">
                <a
                  href={CONTACT.primary.tel}
                  data-testid="side-primary-call"
                  aria-label={`Call primary number: ${CONTACT.primary.display}`}
                  className="flex items-center gap-3 text-navy hover:text-terracotta"
                >
                  <Phone size={16} strokeWidth={1.5}/> {CONTACT.primary.display}
                </a>
                <a
                  href={CONTACT.primary.whatsapp}
                  data-testid="side-primary-whatsapp"
                  aria-label={`Message primary number on WhatsApp: ${CONTACT.primary.display}`}
                  className="flex items-center gap-3 text-navy hover:text-terracotta"
                >
                  <MessageCircle size={16} strokeWidth={1.5}/> WhatsApp — {CONTACT.primary.display}
                </a>
              </div>
            </div>
            <div className="pt-5 border-t border-line">
              <div className="mono text-[10px] uppercase tracking-widest text-prestige" data-testid="side-office-label">
                {CONTACT.office.label}
              </div>
              <div className="mt-3 space-y-2">
                <a
                  href={CONTACT.office.tel}
                  data-testid="side-office-call"
                  aria-label={`Call the office: ${CONTACT.office.display}`}
                  className="flex items-center gap-3 text-navy hover:text-terracotta"
                >
                  <Phone size={16} strokeWidth={1.5}/> {CONTACT.office.display}
                </a>
              </div>
            </div>
          </div>
          <div
            className="p-6 border border-line bg-white"
            data-testid="clinic-address-new-delhi"
          >
            <div className="mono text-[10px] uppercase tracking-[0.22em] text-prestige">
              New Delhi Clinic — In-Person Consultation
            </div>
            <address className="not-italic text-[14.5px] text-navy mt-3 leading-relaxed">
              HIGH RISKK PREGNANCY CLINIC<br/>
              A-78A, Veer Savarkar Marg<br/>
              Opposite SBI Bank, Lajpat Nagar II<br/>
              New Delhi – 110024
            </address>
          </div>
          <div className="p-6 border border-prestige/40 bg-prestige-bg/50">
            <p className="text-sm text-stone2 leading-relaxed">All enquiries are handled with strict confidentiality. This platform does not diagnose or provide emergency care.</p>
          </div>
        </aside>
      </div>
    </Section>
  );
}

function Field({ label, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <div className="mono text-[10px] uppercase tracking-widest text-stone2 mb-1.5">{label}</div>
      {children}
    </label>
  );
}
function Row({ children }) { return <div className="grid sm:grid-cols-2 gap-5">{children}</div>; }
function Step({ n, title, text }) {
  return (
    <li className="flex gap-4">
      <div className="mono text-xs text-prestige-soft border border-prestige-soft/60 w-7 h-7 flex items-center justify-center rounded-full shrink-0">{n}</div>
      <div>
        <div className="font-serif text-lg text-white">{title}</div>
        <div className="text-sm text-white/70">{text}</div>
      </div>
    </li>
  );
}
const inputCls = "w-full bg-white border border-line px-3.5 py-2.5 text-sm focus:outline-none focus:border-navy transition-colors";
